import { useCallback, useRef, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import AskAiChatRepository from '@/creditflowresearch/infrastructure/repository/AskAiChatRepository';
import { IAskAiChatMessage, IAskAiChatMessagesResult } from '@/creditflowresearch/domain/vo/AskAiChat';
import { toMessage } from '../helpers/chatMessages';
import { MESSAGES_PER_PAGE } from '../constants';

interface IParams {
  chatIdRef: RefObject<string | null>,
  setMessages: Dispatch<SetStateAction<IAskAiChatMessage[]>>,
  setConnectionError: (message: string | null) => void,
}

const useAskAiChatMessageLoader = ({ chatIdRef, setMessages, setConnectionError }: IParams) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const nextBeforeRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const repository = getContainer().get<AskAiChatRepository>(AskAiChatRepository);

  const reset = useCallback((): void => {
    nextBeforeRef.current = null;
    isFetchingRef.current = false;
    setIsInitializing(false);
    setIsFetchingMessages(false);
    setHasMoreMessages(false);
  }, []);

  const showLatestPage = useCallback(({ items, nextBefore, hasMore }: IAskAiChatMessagesResult): void => {
    nextBeforeRef.current = nextBefore;
    setHasMoreMessages(hasMore);
    setMessages(items.map(toMessage));
  }, [setMessages]);

  const loadChat = useCallback(async (id: string): Promise<void> => {
    setIsInitializing(true);

    try {
      const page = await repository.getMessages(id, MESSAGES_PER_PAGE);

      if (chatIdRef.current !== id) return;

      showLatestPage(page);
      setIsInitializing(false);
    } catch (error) {
      if (chatIdRef.current !== id) return;

      setConnectionError(getErrorMessage(error));
      setMessages([]);
      setHasMoreMessages(false);
      setIsInitializing(false);
    }
  }, [chatIdRef, repository, setMessages, setConnectionError, showLatestPage]);

  const reloadChat = useCallback(async (id: string): Promise<void> => {
    try {
      const page = await repository.getMessages(id, MESSAGES_PER_PAGE);

      if (chatIdRef.current === id) {
        showLatestPage(page);
      }
    } catch (error) {
      if (chatIdRef.current === id) {
        setConnectionError(getErrorMessage(error));
      }
    }
  }, [chatIdRef, repository, setConnectionError, showLatestPage]);

  const loadOlderMessages = useCallback(async (): Promise<void> => {
    const id = chatIdRef.current;
    if (!id || !hasMoreMessages || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetchingMessages(true);

    try {
      const { items, nextBefore, hasMore } = await repository
        .getMessages(id, MESSAGES_PER_PAGE, nextBeforeRef.current);

      if (chatIdRef.current !== id) return;

      nextBeforeRef.current = nextBefore;
      setHasMoreMessages(hasMore);
      setMessages((prev) => [...items.map(toMessage), ...prev]);
    } catch (error) {
      if (chatIdRef.current !== id) return;

      setConnectionError(getErrorMessage(error));
    } finally {
      if (chatIdRef.current === id) {
        isFetchingRef.current = false;
        setIsFetchingMessages(false);
      }
    }
  }, [chatIdRef, hasMoreMessages, repository, setMessages, setConnectionError]);

  return {
    isInitializing,
    isFetchingMessages,
    hasMoreMessages,
    loadChat,
    reloadChat,
    loadOlderMessages,
    reset,
  };
};

export default useAskAiChatMessageLoader;
